import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "../api";
import { Endpoints } from "../api/endpoints";
import { fileNameFormatter } from "../helper";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";
import { extractApiError, getErrorTitle, getErrorMessage } from "../errors";
import { emitDebugEvent } from "@utils/debug/eventIntegration";

/**
 * User type for document upload
 */
export type DocumentUserType = "taxClient" | "spouse" | "dependent";

/**
 * Parameters for document upload
 */
export interface UploadDocumentParams {
  file: File;
  clientId: string;
  taxYear: number;
  userType: DocumentUserType;
  formType: string;
  /**
   * Whether this is a custom organizer question upload
   * @default false
   */
  isCustomQuestion?: boolean;
  /**
   * Whether to skip automatic OCR job launch
   * @default false (OCR runs automatically)
   */
  skipOcr?: boolean;
  /**
   * Password for encrypted PDFs (optional)
   * If provided, the backend will attempt to decrypt the PDF before processing
   */
  password?: string;
}

/**
 * Options for the useDocumentUpload hook
 */
export interface UseDocumentUploadOptions {
  /**
   * Callback when upload succeeds
   */
  onSuccess?: (data: any) => void;
  /**
   * Callback when upload fails
   */
  onError?: (error: Error) => void;
  /**
   * Whether to show notifications
   * @default true
   */
  showNotifications?: boolean;
  /**
   * Custom notification ID
   * @default "document-upload"
   */
  notificationId?: string;
}

/**
 * Upload response from backend
 */
interface UploadResponse {
  statusCode: number;
  message: string;
  content: {
    doc: {
      docId: string;
      fileName: string;
      documentType: string;
      presignedUrl: string;
    };
    job?: {
      id: string;
      status: string;
    };
  };
}

/**
 * Convert File to base64 string
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Extract base64 data without the data URL prefix
      const base64 = reader.result?.toString().split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Unified document upload hook using base64 JSON POST
 *
 * Replaces the old presigned URL pattern with direct backend upload:
 * - No more polling needed (backend returns immediately)
 * - Single request instead of two (get presigned URL + upload to S3)
 * - Backend-controlled upload (better security and error handling)
 *
 * @example
 * ```typescript
 * const { uploadDocument, isUploading } = useDocumentUpload({
 *   onSuccess: () => console.log("Upload complete!"),
 *   showNotifications: true,
 * });
 *
 * uploadDocument({
 *   file: selectedFile,
 *   clientId: "user-123",
 *   taxYear: 2024,
 *   userType: "taxClient",
 *   formType: "w2",
 * });
 * ```
 */
export const useDocumentUpload = (options: UseDocumentUploadOptions = {}) => {
  const {
    onSuccess,
    onError,
    showNotifications = true,
    notificationId = "document-upload",
  } = options;

  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async (params: UploadDocumentParams) => {
      const {
        file,
        clientId,
        taxYear,
        userType,
        formType,
        isCustomQuestion = false,
        skipOcr = false,
        password,
      } = params;

      // Show uploading notification
      if (showNotifications) {
        notifications.show({
          id: notificationId,
          loading: true,
          title: "Uploading",
          message: "Uploading document...",
          color: "blue",
          autoClose: false,
        });
      }

      // Convert file to base64
      const base64Data = await fileToBase64(file);

      // Choose endpoint and payload based on whether it's a custom question
      const endpoint = isCustomQuestion
        ? Endpoints.customQuestionDocuments
        : Endpoints.documents;

      const payload = isCustomQuestion
        ? {
            clientId,
            taxYear,
            userType,
            questionId: formType, // Send as questionId for custom questions
            fileName: fileNameFormatter(file.name),
            data: base64Data,
            skipOcr,
            ...(password && { password }), // Include password if provided
          }
        : {
            clientId,
            taxYear,
            userType,
            form: formType.toLowerCase(), // Send as form for standard forms
            fileName: fileNameFormatter(file.name),
            data: base64Data,
            skipOcr,
            ...(password && { password }), // Include password if provided
          };

      // Direct upload to backend (no presigned URL needed!)
      const response = await axios.post<UploadResponse>(
        `${backend_url}/${endpoint}`,
        payload,
        AXIOS_CONFIG
      );

      return response.data;
    },
    onSuccess: (data, variables) => {
      // Emit debug event for document processing started if jobId exists (non-blocking)
      // This helps support debug async processing flows
      const jobId = data?.content?.job?.id;
      if (jobId) {
        emitDebugEvent("action", "DOCUMENT_PROCESSING_STARTED", "web", {
          fileName: variables.file.name?.substring(0, 100) || "unknown",
          jobId: jobId,
          formType: variables.formType,
          clientId: variables.clientId,
        });
      }

      // Invalidate relevant queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ["documents", variables.clientId, variables.taxYear],
      });
      queryClient.invalidateQueries({
        queryKey: ["spouseDocuments", variables.clientId, variables.taxYear],
      });
      queryClient.invalidateQueries({
        queryKey: ["taxOrganizerData", variables.clientId, variables.taxYear],
      });

      // Show success notification
      if (showNotifications) {
        notifications.update({
          id: notificationId,
          loading: false,
          icon: <IconCheck size={16} />,
          title: "Complete",
          message: "Your document has been processed.",
          color: "green",
          autoClose: 3000,
        });
      }

      // Call custom success callback
      onSuccess?.(data);
    },
    onError: (error, variables) => {
      console.error("Upload failed:", error);

      // Emit debug event for upload failure (non-blocking)
      // This helps support debug error scenarios
      const { message, type } = extractApiError(error);
      const context: Record<string, string | number | boolean | null> = {
        errorMessage: message?.substring(0, 200) || "Upload failed",
        errorType: type || "unknown",
      };
      if (variables?.file?.name) {
        context.fileName = variables.file.name.substring(0, 100);
      }
      if (variables?.formType) {
        context.formType = variables.formType;
      }
      if (variables?.clientId) {
        context.clientId = variables.clientId;
      }
      emitDebugEvent("error", "DOCUMENT_PROCESSING_FAILED", "web", context);

      // Show error notification with specific error details
      if (showNotifications) {
        const errorTitle = getErrorTitle(type);
        const errorMessageText = getErrorMessage(type, message);

        notifications.update({
          id: notificationId,
          loading: false,
          icon: <IconAlertCircle size={16} />,
          title: errorTitle,
          message: errorMessageText,
          color: "red",
          autoClose: 5000,
        });
      }

      // Call custom error callback
      onError?.(error as Error);
    },
  });

  return {
    /**
     * Upload a document
     */
    uploadDocument: uploadMutation.mutate,
    /**
     * Upload a document (async version with promise)
     */
    uploadDocumentAsync: uploadMutation.mutateAsync,
    /**
     * Whether upload is in progress
     */
    isUploading: uploadMutation.isPending,
    /**
     * Upload error if any
     */
    error: uploadMutation.error,
    /**
     * Upload success status
     */
    isSuccess: uploadMutation.isSuccess,
    /**
     * Uploaded document data
     */
    data: uploadMutation.data,
  };
};

/**
 * Delete a document by ID
 * Archives the document in the backend (soft delete)
 *
 * @param documentId - The backend document ID to delete
 * @returns Promise that resolves when deletion is complete
 *
 * @example
 * ```typescript
 * await deleteDocument("123");
 * ```
 */
export const deleteDocument = async (documentId: string): Promise<void> => {
  await axios.delete(
    `${backend_url}/${Endpoints.deleteDocument}${documentId}/`,
    AXIOS_CONFIG
  );
};

/**
 * Response from the presigned URL endpoint
 */
interface PresignedUrlResponse {
  statusCode: number;
  message: string;
  content: {
    presignedUrl: string;
    fileName: string;
    documentType: string;
    docId: string;
  };
}

/**
 * Fetch a presigned URL for viewing a document
 * Checks permissions and generates a time-limited presigned URL
 *
 * @param documentId - The backend document ID
 * @returns Promise that resolves with presigned URL and document metadata
 *
 * @example
 * ```typescript
 * const { presignedUrl, fileName } = await fetchDocumentPresignedUrl("123");
 * window.open(presignedUrl, '_blank');
 * ```
 */
export const fetchDocumentPresignedUrl = async (
  documentId: string
): Promise<PresignedUrlResponse["content"]> => {
  const response = await axios.get<PresignedUrlResponse>(
    `${backend_url}/${Endpoints.documentPresignedUrl}${documentId}/presigned-url/`,
    AXIOS_CONFIG
  );
  return response.data.content;
};
